import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatChatTitle, formatMessageTime, type ClientChat } from '@/types/chat';
import { MessageSquarePlus, Trash2, Loader2, Globe, User } from 'lucide-react';
import { ConfirmDialog } from '@/components/shared';
import { useClients } from '@/hooks/useClients';

interface ChatSidebarProps {
  chats: ClientChat[];
  activeChatId: string | null;
  isLoading?: boolean;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat?: (chatId: string) => void;
}

export function ChatSidebar({
  chats,
  activeChatId,
  isLoading,
  onSelectChat,
  onNewChat,
  onDeleteChat,
}: ChatSidebarProps) {
  const [chatToDelete, setChatToDelete] = useState<ClientChat | null>(null);
  const { clients } = useClients();

  // Build client ID → name map for scope labels
  const clientNameMap = new Map(clients.map((c) => [c.id, c.name]));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b">
        <Button variant="default" className="w-full justify-start gap-2" onClick={onNewChat}>
          <MessageSquarePlus className="h-4 w-4" />
          New Chat
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {chats.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No conversations yet</p>
          ) : (
            chats.map((chat) => (
              <div
                key={chat.id}
                className={cn(
                  'group flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer',
                  activeChatId === chat.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted',
                )}
                onClick={() => onSelectChat(chat.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{formatChatTitle(chat)}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] px-1.5 py-0 h-4 gap-0.5 font-normal shrink-0',
                        activeChatId === chat.id
                          ? 'border-primary-foreground/30 text-primary-foreground/70'
                          : 'border-border text-muted-foreground',
                      )}
                    >
                      {chat.client_id ? (
                        <>
                          <User className="h-2.5 w-2.5" />
                          <span className="truncate max-w-[80px]">
                            {clientNameMap.get(chat.client_id) ?? 'Client'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Globe className="h-2.5 w-2.5" />
                          Org
                        </>
                      )}
                    </Badge>
                    <span
                      className={cn(
                        'text-[10px] truncate',
                        activeChatId === chat.id
                          ? 'text-primary-foreground/60'
                          : 'text-muted-foreground',
                      )}
                    >
                      {formatMessageTime(chat.updated_at)}
                    </span>
                  </div>
                </div>

                {onDeleteChat && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0',
                      activeChatId === chat.id
                        ? 'hover:bg-primary-foreground/20 text-primary-foreground'
                        : 'hover:bg-destructive/10 text-destructive',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setChatToDelete(chat);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {onDeleteChat && (
        <ConfirmDialog
          open={!!chatToDelete}
          onOpenChange={(open) => !open && setChatToDelete(null)}
          title="Delete chat?"
          description="This will permanently delete this conversation and all its messages. This action cannot be undone."
          onConfirm={() => {
            if (chatToDelete) {
              onDeleteChat(chatToDelete.id);
              setChatToDelete(null);
            }
          }}
          variant="destructive"
          confirmLabel="Delete"
        />
      )}
    </div>
  );
}
